import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { Layout, Menu, Row, Col, Typography, Divider, Button, Select } from 'antd';
import { NavLink, useLocation, Outlet, useNavigate } from 'react-router-dom';
import { Logo } from 'components';
import * as authAction from 'actions/auth-action';
import * as configAction from 'actions/config-action';
import { LogoutOutlined } from '@ant-design/icons';

import { LoadingScreen } from 'components';
import { changeLanguage } from 'config/language';
import { themeColors } from 'theme';
import { useStyleRegister } from '@ant-design/cssinjs';
import { useToken } from 'antd/es/theme/internal';
import { style } from 'theme'
import ValidateLegalRepresentativeButton from 'components/button/validate-legal-representative';
import { isBcerto } from 'utils';
import { useBreakageEntitlement } from 'hooks/useBreakageEntitlement';

const { Sider } = Layout;

const mapStateToProps = (state) => ({
    login: state.authReducer.loginReducer,
    config: state.configReducer
});

const mapDispatchToProps = (dispatch) =>
    bindActionCreators({
        ...authAction,
        ...configAction
    },
        dispatch
    );

const LoggedRoutes = (props) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { login, config, getProfileAction, logoutAction, getConfig, loginCompanyAction } = props;
    const [theme, token, hashId] = useToken()
    const [loading, setLoading] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [hasShareholderIssues, setHasShareholderIssues] = useState(false);
    const breakageIdentityKey = login?.user?.id ?? login?.user?.email ?? null;
    const {
        enabled: breakageEnabled,
        loading: breakageEntitlementLoading,
        unavailable: breakageUnavailable,
        unauthorized: breakageUnauthorized,
        forbidden: breakageForbidden,
        scopeMismatch: breakageScopeMismatch,
        kind: breakageEntitlementKind,
    } = useBreakageEntitlement(login.company?.id, {
        identityKey: breakageIdentityKey,
    });


    useStyleRegister(
        {
            theme,
            path: ['.alymente'],
            hashId,
            token: { _tokenKey: 'root' },
        },
        () => (style),
    );

    const load = async () => {
        setLoading(true);
        try {
            await getProfileAction();
            await getConfig();
        } finally {
            setLoading(false);
        }
    }

    React.useEffect(() => {
        load();
    }, []);

    const onChangeCompany = async (companyID = null) => {
        setLoading(true);
        try {
            await loginCompanyAction(companyID === null ? login.company.id : companyID);
            load();
            navigate('/home');
        } finally {
            setLoading(false);
        }
    }

    const selectedMenuChange = () => {
        const path = location.pathname;
        return config.menu
            .filter((x) => {
                if (!x) return false;
                if (path.indexOf(x.url) === 0) return true;
                else if (x.items) return x.items.filter((item) => item.url === path).length > 0;
                else return false;
            })
            .map((x) => x.url);
    };

    React.useEffect(() => {
        if (login.user && login.company) {
            changeLanguage(login.user.language);
        }

        if (!login.isProcessing && login.company && !login.company.master) {
            setSelectedCompany(login.company.id);
        }
    }, [login])


    const isLoading = !login.user || !login.company || config.menu.length === 0 || config.system.length === 0 || loading;

    if (isLoading) {
        return <LoadingScreen />;
    } else {
        return (
            <Layout
                className='alymente'
                style={{
                    minHeight: '100vh',
                    maxHeight: '100vh',
                    height: '100vh',
                    backgroundColor: themeColors.softColor
                }}>
                <Sider
                    className='alymente-sider'
                    width={300}
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        maxHeight: '100vh',
                        position: 'fixed',
                        left: 0,
                        top: 0,
                        maxHeight: '100vh',
                        backgroundColor: themeColors.softColor,
                        '.antLayoutSiderChildren': {
                            backgroundColor: 'red'
                        }
                    }}
                >
                    <Row style={{ padding: "20px 24px" }}>
                        <Col span={24} >
                            <Logo height={45} justify={"center"} />
                        </Col>
                    </Row>
                    <Row gutter={[0, 12]} style={{ padding: "10px 24px" }}>
                        {
                            login.children && login.children.has &&
                            <Select
                                className='alymente-select'
                                disabled={loading || login.isProcessing}
                                loading={loading}
                                placeholder="Selecione uma empresa"
                                title="Trocar de Filial"
                                value={selectedCompany}
                                onChange={(value) => onChangeCompany(value)}
                            >
                                {login.children.data.map((child, index) => <Select.Option key={index} style={{
                                    color: themeColors.darkColor,
                                }} value={child.id}>{child.name}</Select.Option>)}
                            </Select>
                        }
                        <Col span={24}>
                            <Typography.Text style={{ color: '#6f6f6f' }}>{login.user.name}</Typography.Text>
                        </Col>
                    </Row>
                    <Divider style={{ margin: "10px 0", borderTop: '1px solid rgba(0, 0, 0, 0.10)' }} />
                    <Col
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'space-between',
                            height: 'calc(100vh - 210px)',

                        }}
                    >
                        <Menu
                            style={{ backgroundColor: themeColors.softColor }}
                            theme="dark"
                            defaultSelectedKeys={['1']}
                            mode='inline'
                            selectedKeys={selectedMenuChange()}>
                            {config.menu.filter(x => !!x).map((x) => {
                                const isBreakageItem = x.context === 'breakage' || x.url === '/breakage';
                                const menuDisabled = isBreakageItem && (
                                    breakageEntitlementLoading ||
                                    breakageUnavailable ||
                                    breakageUnauthorized ||
                                    breakageForbidden ||
                                    breakageScopeMismatch ||
                                    !breakageEnabled
                                );
                                const breakageMenuTitle = (() => {
                                    if (breakageEntitlementLoading) {
                                        return 'Verificando contratação Breakage…';
                                    }
                                    if (breakageUnauthorized) {
                                        return 'Sessão inválida para consultar Breakage';
                                    }
                                    if (breakageScopeMismatch) {
                                        return 'Entitlement Breakage incompatível com a empresa da sessão';
                                    }
                                    if (breakageForbidden) {
                                        return 'Acesso ao entitlement Breakage não permitido para esta identidade';
                                    }
                                    if (breakageUnavailable) {
                                        return 'Breakage temporariamente indisponível';
                                    }
                                    if (breakageEntitlementKind === 'disabled' || !breakageEnabled) {
                                        return 'Disponível quando a empresa contratar o Breakage';
                                    }
                                    return undefined;
                                })();
                                return (
                                    <Menu.Item key={x.url} disabled={menuDisabled}>
                                        {menuDisabled ? (
                                            <span title={breakageMenuTitle}>
                                                {x.label}
                                            </span>
                                        ) : x.external ? (
                                            <a href={x.url} target={'_parent'}>
                                                {x.label}
                                            </a>
                                        ) : (
                                            <NavLink key={x.url} to={x.url}>
                                                {x.label}
                                            </NavLink>
                                        )}
                                    </Menu.Item>
                                )
                            })}
                        </Menu>
                        <>
                            <Button
                                style={{
                                    marginTop: 'auto',
                                }}
                                type="text"
                                onClick={() => {
                                    logoutAction();
                                    navigate('/auth/login');
                                }}
                            >
                                <Row style={{
                                    marginLeft: 10,
                                }}>
                                    <Col style={{
                                        marginRight: 13,
                                    }}>
                                        <LogoutOutlined />
                                    </Col>
                                    <Col>
                                        Sair
                                    </Col>
                                </Row>
                            </Button>
                            {isBcerto && (
                                <Typography.Text style={{
                                    display: 'block',
                                    padding: '0 24px 16px',
                                    color: '#6f6f6f',
                                    fontSize: 11,
                                    textAlign: 'center',
                                }}>
                                    © AlyPlus Tecnologia e Soluções de Pagamentos
                                </Typography.Text>
                            )}
                        </>
                    </Col>

                </Sider>
                <Layout style={{ marginLeft: 300 }}>
                    <Row gutter={16} align="middle" wrap={false}>
                        {hasShareholderIssues && 
                        <Col flex="1" style={{ marginLeft: 30, color: themeColors.mediumColor, textAlign: 'center'}}>
                            Atenção, para poder realizar pedidos de créditos para empresa/participante, 
                            é necessário ajustar o quadro de sócios. Clique no botão "Validar representante legal" 
                            e regularize o cadastro.
                        </Col>
                        }
                        <Col flex="none">
                        <ValidateLegalRepresentativeButton companyId={login.company.id} onStatusChange={setHasShareholderIssues}/>
                        </Col>
                    </Row>
                    {hasShareholderIssues && <Divider />}
                    <Outlet />
                </Layout>
            </Layout >
        );
    }
};
export default connect(mapStateToProps, mapDispatchToProps)(LoggedRoutes);
